import { evaluate, computeMortgage, RULES, type Evaluation } from '@finavest/mortgage-math';
import { chf, pct, parseAmount, formatThousands } from './format';
import { VERDICT_COPY, DISCLAIMER, FREE_NOTE, CONSENT_TEXT_VERSION } from './copy';
import { renderTurnstile, type TurnstileWidget } from './turnstile';
import { submitLead, submitBeratung, ApiError } from './api';
import { attributionFuerSubmit } from './attribution';
import {
  track,
  bindFieldTouch,
  amountBucket,
  incomeBucket,
  ltvBucket,
  tragbarkeitBucket,
} from './analytics';
import type { CalculatorInputPayload, LeadPayload, BeratungPayload, Contact } from './types';

const NOTE_100 =
  '<strong>100%-Finanzierung:</strong> Sie müssen mindestens 10% als Eigenmittel besitzen ' +
  '(nachweisbar auf dem Konto) – nicht als Anzahlung. Solche Lösungen sind über unsere Partner ' +
  'möglich, abhängig von Tragbarkeit und Bonität. Wir prüfen das kostenlos.';
const NOTE_80 =
  'Finanzierungen über 80% Belehnung sind über unsere Partner möglich – abhängig von ' +
  'Tragbarkeit und Bonität. Wir prüfen das kostenlos für Sie.';

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));
const round = (n: number, d: number): number => {
  const f = 10 ** d;
  return Math.round(n * f) / f;
};
const isEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
/** Machine-readable analytics reason for a failed submit (no user input echoed). */
const apiErrorReason = (err: unknown): string =>
  err instanceof ApiError ? (err.status ? `http_${err.status}` : 'network') : 'unknown';

class Calculator {
  private price = 0;
  private income = 0;
  private equity = 0;
  private activePreset: number | null = null;

  private currentEval: Evaluation | null = null;
  private leadId: string | undefined;
  private contact: Contact | null = null;

  private gateWidget: TurnstileWidget | undefined;
  private beratungWidget: TurnstileWidget | undefined;
  /** One gate submit in flight (RV616 m3) — see onGate. */
  private gateBusy = false;

  /** Last amount per analytics field — field_change fires on commit only when changed. */
  private readonly lastTracked: Record<string, number> = {};

  // hot elements
  private readonly priceEl = this.input('in-price');
  private readonly incomeEl = this.input('in-income');
  private readonly equityEl = this.input('in-equity');

  init(): void {
    // amount inputs: live parse + Swiss formatting on blur
    this.bindAmount(this.priceEl, (n) => {
      this.price = n;
      if (this.activePreset !== null) this.applyPreset(this.activePreset, false);
      this.clearFieldError('price');
      this.updateGauges();
    });
    this.bindAmount(this.incomeEl, (n) => {
      this.income = n;
      this.clearFieldError('income');
      this.updateGauges();
    });
    this.bindAmount(this.equityEl, (n) => {
      this.equity = n;
      this.activePreset = null;
      this.clearPresetActive();
      this.updateGauges();
    });

    // equity presets
    for (const btn of this.el('eq-presets').querySelectorAll<HTMLButtonElement>(
      'button[data-eq]',
    )) {
      btn.addEventListener('click', () => {
        const p = parseFloat(btn.dataset.eq ?? '0');
        this.applyPreset(p, true);
        this.setPresetActive(btn);
        this.updateGauges();
        track('rechner_field_change', {
          field: 'eigenmittel_preset',
          value: String(Math.round(p * 100)),
        });
      });
    }

    this.form('calc-form').addEventListener('submit', (e) => this.onCalc(e));
    this.form('gate-form').addEventListener('submit', (e) => void this.onGate(e));
    this.el('beratung-open').addEventListener('click', () => void this.onBeratungOpen());
    this.form('beratung-form').addEventListener('submit', (e) => void this.onBeratung(e));

    this.bindAnalytics();
    this.bindFormFieldAnalytics();
    track('rechner_loaded');
    track('rechner_step_view', { step: 'input' });
  }

  /**
   * Per-field engagement for the lead-gate and Beratung forms (GTM funnel
   * drop-off). Fire-only on first touch — only the field NAME is emitted, never
   * the value (PII stays in the form). The form sections exist in the DOM from
   * the start (hidden), so binding here is safe.
   */
  private bindFormFieldAnalytics(): void {
    bindFieldTouch('rechner_gate_field', [
      { field: 'vorname', el: document.getElementById('g-first') },
      { field: 'nachname', el: document.getElementById('g-last') },
      { field: 'email', el: document.getElementById('g-email') },
      { field: 'consent', el: document.getElementById('g-consent') },
    ]);
    bindFieldTouch('rechner_beratung_field', [
      { field: 'telefon', el: document.getElementById('b-phone') },
      { field: 'strasse', el: document.getElementById('b-street') },
      { field: 'plz', el: document.getElementById('b-zip') },
      { field: 'ort', el: document.getElementById('b-city') },
      { field: 'zeit', el: document.getElementById('b-time') },
      { field: 'sprache', el: document.getElementById('b-lang') },
    ]);
  }

  /**
   * Analytics-only listeners (GTM spec). Amounts fire on commit (blur), never per
   * keystroke, and ONLY as bucket strings; selects fire their enum value. The PLZ
   * field is deliberately NOT tracked.
   */
  private bindAnalytics(): void {
    const amounts: [string, HTMLInputElement, (n: number) => string][] = [
      ['kaufpreis', this.priceEl, amountBucket],
      ['einkommen', this.incomeEl, incomeBucket],
      ['eigenmittel', this.equityEl, amountBucket],
    ];
    for (const [field, el, bucket] of amounts) {
      el.addEventListener('blur', () => {
        const n = parseAmount(el.value);
        if (n <= 0 || n === this.lastTracked[field]) return;
        this.lastTracked[field] = n;
        track('rechner_field_change', { field, value_bucket: bucket(n) });
      });
    }

    const enums: [string, string][] = [
      ['objekttyp', 'in-objekt'],
      ['verwendung', 'in-verwendung'],
      ['kanton', 'in-kanton'],
      ['situation', 'in-situation'],
    ];
    for (const [field, id] of enums) {
      this.el(id).addEventListener('change', () => {
        const v = this.val(id);
        if (v) track('rechner_field_change', { field, value: v.toLowerCase() });
      });
    }
  }

  // ---------- input helpers ----------

  private bindAmount(el: HTMLInputElement, onChange: (n: number) => void): void {
    el.addEventListener('input', () => onChange(parseAmount(el.value)));
    el.addEventListener('focus', () => {
      const n = parseAmount(el.value);
      el.value = n > 0 ? String(n) : '';
    });
    el.addEventListener('blur', () => {
      el.value = formatThousands(parseAmount(el.value));
    });
  }

  private applyPreset(percent: number, fromClick: boolean): void {
    this.activePreset = percent;
    this.equity = Math.round(this.price * percent);
    this.equityEl.value = formatThousands(this.equity);
    // Preset-driven equity is tracked via `eigenmittel_preset`, not as a field change.
    this.lastTracked['eigenmittel'] = this.equity;
    if (fromClick) this.equityEl.blur();
  }

  private setPresetActive(active: HTMLButtonElement): void {
    for (const b of this.el('eq-presets').querySelectorAll('button'))
      b.classList.remove('is-active');
    active.classList.add('is-active');
  }
  private clearPresetActive(): void {
    for (const b of this.el('eq-presets').querySelectorAll('button'))
      b.classList.remove('is-active');
  }

  // ---------- live gauges + guidance ----------

  private updateGauges(): void {
    const ready = this.price > 0 && this.income > 0;
    this.el('gauges').hidden = !ready;

    if (ready) {
      const r = computeMortgage(this.price, this.income, this.equity);
      // Tragbarkeit: domain 0..50% maps to 0..100%, threshold (1/3) marked at 66.6%.
      this.el('m-trag-fill').style.width = clamp(r.ratio / 0.5, 0, 1) * 100 + '%';
      this.text('m-trag-val', Number.isFinite(r.ratio) ? pct(r.ratio, 0) : '–');
      this.el('m-trag').dataset.status =
        r.ratio <= RULES.greenMax ? 'ok' : r.ratio <= RULES.affordabilityMax ? 'warn' : 'bad';

      // Eigenmittel: domain 0..40% maps to 0..100%, threshold 20% marked at 50%.
      const eqPct = this.equity / this.price;
      this.el('m-eq-fill').style.width = clamp(eqPct / 0.4, 0, 1) * 100 + '%';
      this.text('m-eq-val', pct(eqPct, 0));
      this.el('m-eq').dataset.status =
        eqPct >= 0.2 ? 'ok' : eqPct >= RULES.minOwnedEquity ? 'warn' : 'special';
    }

    this.updateGuidance();
  }

  private updateGuidance(): void {
    const g = this.el('guidance');
    if (this.price <= 0) {
      g.hidden = true;
      g.innerHTML = '';
      return;
    }
    const eqPct = this.equity / this.price;
    let msg = '';
    let tone = 'special';

    if (eqPct < RULES.minOwnedEquity) {
      msg =
        `Für eine Finanzierung sollten Sie i.&nbsp;d.&nbsp;R. mindestens 10% Eigenmittel besitzen ` +
        `(${chf(this.price * RULES.minOwnedEquity)}). Mit weniger ist über unsere Partner oft eine ` +
        `<strong>Spezialprüfung</strong> möglich – wir prüfen das kostenlos.`;
    } else if (eqPct < 0.2) {
      msg =
        `Standardbanken verlangen ~20% Eigenmittel (${chf(this.price * 0.2)}). Mit weniger ist eine ` +
        `Finanzierung über unsere Partner ggf. möglich – wir prüfen das (<strong>Spezialprüfung</strong>).`;
    } else if (this.income > 0) {
      const r = computeMortgage(this.price, this.income, this.equity);
      if (r.ratio > RULES.affordabilityMax) {
        tone = 'warn';
        msg =
          'Die kalkulatorische Tragbarkeit liegt über dem Richtwert von 33%. Mehr Eigenmittel oder ' +
          'eine Spezialprüfung können helfen – das schauen wir gerne mit Ihnen an.';
      }
    }

    if (msg) {
      g.hidden = false;
      g.dataset.tone = tone;
      g.innerHTML = msg;
    } else {
      g.hidden = true;
      g.innerHTML = '';
    }
  }

  // ---------- step 1: compute ----------

  private onCalc(e: SubmitEvent): void {
    e.preventDefault();
    track('rechner_calculate_click');
    const okPrice = this.price > 0;
    const okIncome = this.income > 0;
    this.setFieldError('price', !okPrice);
    this.setFieldError('income', !okIncome);
    if (!okPrice || !okIncome) {
      (okPrice ? this.incomeEl : this.priceEl).focus();
      return;
    }

    const ev = evaluate(this.price, this.income, this.equity);
    this.currentEval = ev;
    // Capture visibility BEFORE rendering — step views fire on reveal, not re-render.
    const resultWasHidden = this.el('result').hidden;
    const gateWasVisible = !resultWasHidden && !this.el('gate').hidden;
    this.renderResult(ev);
    this.el('result').hidden = false;
    track('rechner_verdict_shown', {
      verdict: ev.verdict,
      ltv_bucket: ltvBucket(ev.result.ltv),
      tragbarkeit_bucket: tragbarkeitBucket(ev.result.ratio),
    });
    if (resultWasHidden) track('rechner_step_view', { step: 'result' });
    if (!gateWasVisible) {
      track('rechner_step_view', { step: 'gate' });
      track('rechner_gate_view');
    }
    void this.renderGateTurnstile();
    this.el('result').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private renderResult(ev: Evaluation): void {
    const { result: r, verdict } = ev;
    this.el('verdict').className = 'verdict verdict--' + verdict;
    this.text('verdict-title', VERDICT_COPY[verdict].title);
    this.text('verdict-copy', VERDICT_COPY[verdict].copy);

    const teaser = this.el('figures-teaser');
    teaser.innerHTML = '';
    this.addFig(teaser, 'Monatliche Wohnkosten (kalkulatorisch)', chf(r.monthly));
    this.addFig(teaser, 'Belehnung', pct(r.ltv));
    this.addFig(teaser, 'Wohnkostenquote (Richtwert max. 33%)', pct(r.ratio));

    const eqPct = this.price > 0 ? this.equity / this.price : 0;
    const note = this.el('note');
    if (eqPct <= 0) {
      note.hidden = false;
      note.innerHTML = NOTE_100;
    } else if (eqPct < 0.2) {
      note.hidden = false;
      note.innerHTML = NOTE_80;
    } else {
      note.hidden = true;
      note.innerHTML = '';
    }

    // Fresh calc → reset gate/detail visibility (keep captured lead if any).
    this.el('gate').hidden = false;
    this.el('detail').hidden = true;
  }

  // ---------- step 2: lead gate ----------

  private async renderGateTurnstile(): Promise<void> {
    if (!this.gateWidget) {
      this.gateWidget = await renderTurnstile(this.el('ts-gate'), 'finavest-lead').catch(
        () => undefined,
      );
    }
  }

  private async onGate(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    // RV616 m3: one submit in flight. The consent question below waits up to
    // CONSENT_TIMEOUT_MS for the host, and a second click in that window sent
    // the same Turnstile token again: the backend refused it AFTER the first
    // one had minted the lead, and the visitor saw an error on a success.
    if (this.gateBusy) return;
    track('rechner_gate_submit');
    const first = this.val('g-first');
    const last = this.val('g-last');
    const email = this.val('g-email');
    const consent = this.input('g-consent').checked;

    // Error reasons are machine codes only — never echo what the user typed.
    if (!first || !last)
      return this.gateFail('missing_name', 'Bitte geben Sie Vor- und Nachnamen an.');
    if (!isEmail(email))
      return this.gateFail('invalid_email', 'Bitte geben Sie eine gültige E-Mail-Adresse an.');
    if (!consent) return this.gateFail('missing_consent', 'Bitte bestätigen Sie die Einwilligung.');
    const token = this.gateWidget?.getToken() ?? '';
    if (!token)
      return this.gateFail(
        'missing_turnstile',
        'Bitte bestätigen Sie, dass Sie kein Roboter sind.',
      );
    this.gateErr('');

    const ev = this.currentEval!;
    const r = ev.result;
    this.contact = { first, last, email };

    // Locked BEFORE the consent question, not after it (RV616 m3).
    this.gateBusy = true;
    try {
      await this.withLoading(this.button('gate-btn'), async () => {
        // utm_* + Google click ids off the (iframe) URL; the click id only when
        // the host says the ads choice was not refused (RV614 M3) — asked NOW.
        const utm = await attributionFuerSubmit(location.search);
        // Canonical @finavest/shared leadIngestSchema shape. The worker validates
        // with the shared schema and the backend re-validates + verifies Turnstile.
        const payload: LeadPayload = {
          firstName: first,
          lastName: last,
          email,
          locale: 'de',
          consent: {
            privacyPolicy: true,
            marketingEmail: false,
            textVersion: CONSENT_TEXT_VERSION,
          },
          turnstileToken: token,
          calculator: {
            input: this.collectInput(),
            result: {
              monthlyCost: Math.round(r.monthly),
              loanToValuePct: round(r.ltv * 100, 2),
              affordabilityRatioPct: round(clamp(r.ratio * 100, 0, 500), 2),
              affordable: ev.verdict === 'machbar' || ev.verdict === 'knapp',
              verdict: ev.verdict,
              ratio: round(r.ratio, 4),
              ltv: round(r.ltv, 4),
              maxAffordablePrice: Math.round(r.maxPrice),
              ruleVersion: ev.ruleVersion,
            },
          },
          source: 'tools_calculator',
          ...(utm ? { utm } : {}),
        };
        try {
          const { leadId } = await submitLead(payload);
          this.leadId = leadId;
          track('rechner_gate_success');
          this.revealDetail(email);
        } catch (err) {
          track('rechner_gate_error', { reason: apiErrorReason(err) });
          this.gateErr(err instanceof ApiError ? err.message : 'Es ist ein Fehler aufgetreten.');
          this.gateWidget?.reset();
        }
      });
    } finally {
      this.gateBusy = false;
    }
  }

  private collectInput(): CalculatorInputPayload {
    const ci: CalculatorInputPayload = {
      type: 'affordability',
      propertyValue: this.price,
      annualIncome: this.income,
      equity: this.equity,
    };
    const kanton = this.val('in-kanton');
    if (kanton) ci.canton = kanton;
    // W1 extras — survive via the schema's .passthrough()
    type OptKey = 'objektTyp' | 'verwendung' | 'plz' | 'situation';
    const opt: [OptKey, string][] = [
      ['objektTyp', 'in-objekt'],
      ['verwendung', 'in-verwendung'],
      ['plz', 'in-plz'],
      ['situation', 'in-situation'],
    ];
    for (const [key, id] of opt) {
      const v = this.val(id);
      if (v) ci[key] = v;
    }
    return ci;
  }

  private revealDetail(email: string): void {
    this.el('gate').hidden = true;
    this.el('detail').hidden = false;
    this.text(
      'sent-note',
      `Ihre Angaben sind eingegangen. Die vollständige Auswertung sehen Sie unten – ` +
        `eine Kopie senden wir an ${email}.`,
    );

    const r = this.currentEval!.result;
    const f = this.el('figures-full');
    f.innerHTML = '';
    this.addFig(f, 'Hypothek', chf(r.mortgage));
    this.addFig(f, 'davon 2. Hypothek (amortisationspflichtig)', chf(r.second));
    this.addFig(f, 'Belehnung', pct(r.ltv));
    this.addFig(f, 'Kalkulatorischer Zins (5% p.a.)', chf(r.interest) + ' / Jahr');
    this.addFig(f, 'Amortisation 2. Hypothek', chf(r.amort) + ' / Jahr');
    this.addFig(f, 'Nebenkosten (1%)', chf(r.maint) + ' / Jahr');
    this.addFig(f, 'Monatliche Wohnkosten (kalkulatorisch)', chf(r.monthly));
    this.addFig(f, 'Wohnkostenquote (Richtwert max. 33%)', pct(r.ratio));
    this.addFig(f, 'Eigenmittel im Besitz (mind. 10%)', chf(this.price * RULES.minOwnedEquity));
    this.addFig(f, 'Max. tragbarer Kaufpreis (Richtwert)', chf(r.maxPrice));

    this.text('disclaimer', DISCLAIMER);
    this.text('free-note', FREE_NOTE);
    // Detail reveal also surfaces the Beratung CTA → next funnel step in view.
    track('rechner_step_view', { step: 'beratung' });
    this.el('detail').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ---------- step 3: Beratung (→ warm) ----------

  private async onBeratungOpen(): Promise<void> {
    track('rechner_beratung_click');
    this.el('beratung-cta').hidden = true;
    this.el('beratung-form').hidden = false;
    if (!this.beratungWidget) {
      this.beratungWidget = await renderTurnstile(
        this.el('ts-beratung'),
        'finavest-beratung',
      ).catch(() => undefined);
    }
    this.el('beratung-form').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private async onBeratung(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    track('rechner_beratung_submit');
    const phone = this.val('b-phone');
    if (!phone) return this.beratungFail('missing_phone', 'Bitte geben Sie eine Telefonnummer an.');
    // Mirror the shared beratungIngestSchema phone rule so a malformed number
    // gets a clear German message instead of a generic 400 from the worker.
    if (!/^[+0-9 ()/.-]{6,32}$/.test(phone))
      return this.beratungFail('invalid_phone', 'Bitte geben Sie eine gültige Telefonnummer an.');
    const token = this.beratungWidget?.getToken() ?? '';
    if (!token)
      return this.beratungFail(
        'missing_turnstile',
        'Bitte bestätigen Sie, dass Sie kein Roboter sind.',
      );
    this.beratungErr('');

    const c = this.contact ?? { first: '', last: '', email: '' };
    const payload: BeratungPayload = {
      turnstileToken: token,
      ...(this.leadId ? { leadId: this.leadId } : {}),
      firstName: c.first,
      lastName: c.last,
      email: c.email,
      phone,
      // Trim to the shared-schema caps so overlong input never 400s the request.
      address: {
        street: this.val('b-street').slice(0, 120),
        zip: this.val('b-zip').slice(0, 10),
        city: this.val('b-city').slice(0, 80),
      },
      preferredTime: (this.val('b-time') || 'egal').slice(0, 30),
      language: this.val('b-lang') || 'de',
    };

    await this.withLoading(this.button('beratung-btn'), async () => {
      try {
        await submitBeratung(payload);
        track('rechner_beratung_success');
        this.el('beratung-form').hidden = true;
        this.el('beratung-success').hidden = false;
        this.el('beratung-success').scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (err) {
        track('rechner_beratung_error', { reason: apiErrorReason(err) });
        this.beratungErr(err instanceof ApiError ? err.message : 'Es ist ein Fehler aufgetreten.');
        this.beratungWidget?.reset();
      }
    });
  }

  // ---------- small DOM utilities ----------

  private el<T extends HTMLElement = HTMLElement>(id: string): T {
    const node = document.getElementById(id);
    if (!node) throw new Error(`#${id} missing`);
    return node as T;
  }
  private input(id: string): HTMLInputElement {
    return this.el<HTMLInputElement>(id);
  }
  private button(id: string): HTMLButtonElement {
    return this.el<HTMLButtonElement>(id);
  }
  private form(id: string): HTMLFormElement {
    return this.el<HTMLFormElement>(id);
  }
  private text(id: string, value: string): void {
    this.el(id).textContent = value;
  }
  private val(id: string): string {
    return (this.el(id) as HTMLInputElement | HTMLSelectElement).value.trim();
  }

  private addFig(ul: HTMLElement, label: string, value: string): void {
    const li = document.createElement('li');
    const l = document.createElement('span');
    l.className = 'lbl';
    l.textContent = label;
    const v = document.createElement('span');
    v.className = 'val';
    v.textContent = value;
    li.append(l, v);
    ul.appendChild(li);
  }

  private setFieldError(name: string, on: boolean): void {
    const field = document.querySelector(`.field[data-field="${name}"]`);
    field?.classList.toggle('has-error', on);
  }
  private clearFieldError(name: string): void {
    this.setFieldError(name, false);
  }

  private gateErr(msg: string): void {
    const e = this.el('gate-error');
    e.textContent = msg;
    e.hidden = !msg;
  }
  private beratungErr(msg: string): void {
    const e = this.el('beratung-error');
    e.textContent = msg;
    e.hidden = !msg;
  }
  /** Show a gate validation error + emit its machine-code reason (never the input). */
  private gateFail(reason: string, msg: string): void {
    track('rechner_gate_error', { reason });
    this.gateErr(msg);
  }
  private beratungFail(reason: string, msg: string): void {
    track('rechner_beratung_error', { reason });
    this.beratungErr(msg);
  }

  private async withLoading(btn: HTMLButtonElement, run: () => Promise<void>): Promise<void> {
    const label = btn.textContent;
    btn.disabled = true;
    btn.dataset.loading = 'true';
    btn.textContent = 'Senden…';
    try {
      await run();
    } finally {
      btn.disabled = false;
      delete btn.dataset.loading;
      btn.textContent = label;
    }
  }
}

export function initCalculator(): void {
  new Calculator().init();
}
